import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { addToConfig } from './utils';
import { selectConfigFlowPages } from '../../../selectors';
import ProductOptionsSelector from '../products/regular/subcomponents/ProductOptionsSelector';
import { setCurrentProduct } from '../../../reducers/productConfigSlice';
import { MOBILE_L } from '../hooks/screen';

const ProductOptionsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px;
  max-width: 600px;
  margin: 0 auto;
  width: 100%;

  @media (max-width: ${MOBILE_L}px) {
    padding: 15px;
  }
`;

const ProductInfo = styled.div`
  text-align: center;
  margin-bottom: 30px;

  h2 {
    font-size: 24px;
    margin-bottom: 10px;
    color: #333;

    @media (max-width: ${MOBILE_L}px) {
      font-size: 20px;
    }
  }

  p {
    font-size: 16px;
    color: #666;
    margin-bottom: 5px;
  }
`;

const ProductImage = styled.img`
  width: 200px;
  height: auto;
  margin-bottom: 20px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

  @media (max-width: ${MOBILE_L}px) {
    width: 150px;
  }
`;

const ButtonWrapper = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 30px;
  width: 100%;
  justify-content: center;

  @media (max-width: ${MOBILE_L}px) {
    flex-direction: column;
  }
`;

const ActionButton = styled.button`
  border: none;
  background: #479de3;
  color: white;
  padding: 12px 24px;
  font-size: 16px;
  font-weight: 600;
  border-radius: 4px;
  cursor: pointer;
  min-width: 120px;
  transition: background-color 0.2s ease;

  &:hover {
    background: #3885d3;
  }

  &:disabled {
    background: #ccc;
    cursor: not-allowed;
  }

  @media (max-width: ${MOBILE_L}px) {
    width: 100%;
    padding: 14px 24px;
  }
`;

const SkipButton = styled(ActionButton)`
  background: #e0e0e0;
  color: #333;

  &:hover {
    background: #d0d0d0;
  }
`;

export const ProductOptions = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const history = useHistory();
  const shoot = useSelector((state) => state.shoot);
  const productConfig = useSelector((state) => state.v3Order.productConfig);
  const additionalData = useSelector((state) => state.additionalData);
  const pages = useSelector(selectConfigFlowPages);
  const selectedProductOptions = useSelector(
    (state) => state.productConfig.selectedProductOptions,
  );

  const [skipTriggered, setSkipTriggered] = useState(false);

  // Get the current product from productConfig
  const currentIndex = parseInt(location.pathname.split('/').pop(), 10);

  const product = productConfig[currentIndex]?.product;

  const hasOptions =
    product?.product_option_sets?.length > 0 ||
    product?.product_options?.length > 0;
  const hasRequiredOptionSets =
    product?.product_option_sets?.some(
      (optionSet) => optionSet.selection_required,
    ) || false;

  const handleContinue = () => {
    // Persist the options the user selected (held in Redux by
    // ProductOptionsSelector) with the product configuration.
    addToConfig({
      dispatch,
      history,
      productConfig,
      pages,
      itemToAdd: selectedProductOptions,
      event: shoot,
    });
  };

  const handleSkip = () => {
    // Continue without selecting options
    addToConfig({
      dispatch,
      history,
      productConfig,
      pages,
      itemToAdd: {},
      event: shoot,
    });
  };

  // Keep the Redux currentProduct in sync so ProductOptionsSelector (which
  // reads currentProduct) renders this product's option sets. setCurrentProduct
  // also seeds/reconciles the default option selections.
  useEffect(() => {
    if (product) {
      dispatch(setCurrentProduct(product));
    }
  }, [product, dispatch]);

  // Auto-skip to the next step when there is no product to configure.
  useEffect(() => {
    if (!product && !skipTriggered) {
      setSkipTriggered(true);
      addToConfig({
        dispatch,
        history,
        productConfig,
        pages,
        itemToAdd: {},
        event: shoot,
      });
    }
  }, [
    product,
    skipTriggered,
    dispatch,
    history,
    productConfig,
    shoot,
    additionalData,
  ]);

  // Auto-skip when the product has no options to configure.
  useEffect(() => {
    if (product && !hasOptions && !skipTriggered) {
      setSkipTriggered(true);
      addToConfig({
        dispatch,
        history,
        productConfig,
        pages,
        itemToAdd: {},
        event: shoot,
      });
    }
  }, [
    product,
    hasOptions,
    skipTriggered,
    dispatch,
    history,
    productConfig,
    shoot,
    additionalData,
  ]);

  // If no product, return null (the effect above handles the skip)
  if (!product) {
    return null;
  }

  return (
    <ProductOptionsWrapper>
      <ProductInfo>
        <h2>Configure Your Selection</h2>
        {product.image_thumbnail && (
          <ProductImage src={product.image_thumbnail} alt={product.name} />
        )}
        <p>
          <strong>{product.name}</strong>
        </p>
        <p>Please select your options below:</p>
      </ProductInfo>

      <ProductOptionsSelector />

      <ButtonWrapper>
        {!hasRequiredOptionSets && (
          <SkipButton onClick={handleSkip}>Skip Options</SkipButton>
        )}
        <ActionButton onClick={handleContinue}>Continue</ActionButton>
      </ButtonWrapper>
    </ProductOptionsWrapper>
  );
};

export default ProductOptions;
